import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors, groupThemes, gradientCss } from "@/lib/theme";
import { commentTime } from "@/lib/date";
import { Avatar, Spinner } from "@/components/peeps/ui";
import { HomeButton } from "@/components/peeps/HomeButton";

export default function GroupRequests() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.listGroupRequests().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const [deduping, setDeduping] = useState(false);
  const runDedupe = async () => {
    setDeduping(true);
    try {
      const r = await api.dedupeEvents();
      toast(r.duplicates_removed > 0 ? `Removed ${r.duplicates_removed} duplicate event${r.duplicates_removed === 1 ? "" : "s"}` : "No duplicates found");
    } catch (e) {
      toast(e.message || "Couldn't run cleanup");
    } finally {
      setDeduping(false);
    }
  };

  if (user && !user.is_app_admin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: colors.surface }}>
        <p className="font-display text-[15px] mb-4" style={{ color: colors.onSurfaceTertiary }}>This page is for the app admin.</p>
        <button onClick={() => navigate("/")} className="font-display font-medium text-sm underline" style={{ color: colors.brandSecondary }}>Back home</button>
      </div>
    );
  }

  const decide = async (id, action) => {
    setBusy(id);
    try {
      if (action === "approve") { const r = await api.approveGroupRequest(id); toast("Group approved & created"); }
      else { await api.declineGroupRequest(id); toast("Request declined"); }
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      toast(e.message || "Couldn't update the request");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: colors.surface }}>
      <div className="max-w-2xl mx-auto px-5 py-7 pb-24">
        <HomeButton />
        <h1 className="font-display font-bold text-3xl mt-4 mb-1" style={{ color: colors.onSurface }}>Group requests</h1>
        <p className="font-display text-[14px] mb-4" style={{ color: colors.onSurfaceTertiary }}>Approve or decline groups people want to create.</p>

        <button data-testid="run-dedupe" onClick={runDedupe} disabled={deduping}
          className="mb-6 flex items-center gap-2 rounded-full px-4 py-2.5" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
          <Copy size={14} color={colors.brandSecondary} />
          <span className="font-display font-medium text-[13px]" style={{ color: colors.onSurface }}>{deduping ? "Cleaning up…" : "Remove duplicate events"}</span>
        </button>

        {rows === null ? (
          <div className="py-20"><Spinner /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16" data-testid="requests-empty">
            <Users size={30} color={colors.muted} className="mx-auto mb-3" />
            <p className="font-display text-[15px]" style={{ color: colors.onSurfaceTertiary }}>No pending requests</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="group-requests-list">
            {rows.map((r) => {
              const th = groupThemes[r.theme] || groupThemes.violet;
              return (
                <div key={r.id} data-testid={`request-${r.id}`} className="rounded-2xl p-4" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl shrink-0 flex items-center justify-center font-display font-bold text-lg" style={{ background: gradientCss(th.grad), color: "#fff" }}>
                      {(r.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-[16px] truncate" style={{ color: colors.onSurface }}>{r.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Avatar name={r.requester.display_name} uri={r.requester.profile_photo_url} size={16} />
                        <span className="font-display text-[12.5px] truncate" style={{ color: colors.onSurfaceTertiary }}>{r.requester.display_name} · {commentTime(r.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`approve-${r.id}`} onClick={() => decide(r.id, "approve")} disabled={busy === r.id}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5" style={{ background: colors.brand }}>
                      <Check size={15} color={colors.onBrand} />
                      <span className="font-display font-bold text-[13.5px]" style={{ color: colors.onBrand }}>Approve</span>
                    </button>
                    <button data-testid={`decline-${r.id}`} onClick={() => decide(r.id, "decline")} disabled={busy === r.id}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5" style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
                      <X size={15} color={colors.onSurfaceTertiary} />
                      <span className="font-display font-medium text-[13.5px]" style={{ color: colors.onSurface }}>Decline</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
